import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import GetAppRoundedIcon from '@mui/icons-material/GetAppRounded';
import IosShareRoundedIcon from '@mui/icons-material/IosShareRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import { usePwaInstall } from '../../hooks/usePwaInstall';

/**
 * "Download the app" entry point intended for the site footer.
 *
 * Behaviour matrix:
 *   • Already installed (standalone)       → renders nothing.
 *   • Android / Chromium with install ready → click triggers the native
 *                                             install sheet directly.
 *   • iOS Safari                            → click opens a small dialog
 *                                             with Share → Add to Home
 *                                             Screen instructions
 *                                             (Apple offers no
 *                                             programmatic install API).
 *   • Any other browser / desktop without
 *     install ready                        → click opens the same dialog
 *                                             with generic guidance for
 *                                             both platforms, so the
 *                                             link is never a dead end.
 */
export function InstallAppLink() {
  const { t } = useTranslation();
  const { isStandalone, isIos, canInstall, install } = usePwaInstall();
  const [dialogOpen, setDialogOpen] = useState(false);

  if (isStandalone) return null;

  const handleClick = async () => {
    if (canInstall) {
      // Native sheet on Android/Chromium — no need to open the dialog.
      await install();
      return;
    }
    // iOS or any other browser without a fired beforeinstallprompt:
    // show instructions so the user can install manually.
    setDialogOpen(true);
  };

  return (
    <>
      <Link
        component="button"
        type="button"
        onClick={handleClick}
        underline="none"
        sx={{
          color: 'text.secondary',
          fontSize: '0.9375rem',
          lineHeight: 1.4,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          textAlign: 'left',
          transition: 'color 150ms ease',
          '&:hover': { color: 'primary.main' },
        }}
      >
        <GetAppRoundedIcon sx={{ fontSize: '1.05rem' }} />
        {t('pwa.footer.cta')}
      </Link>

      <InstallInstructionsDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        forceIos={isIos}
      />
    </>
  );
}

/**
 * Step-by-step install instructions. Defaults to a two-column iOS +
 * Android view; when {@code forceIos} is true (we know the visitor is
 * on iPhone Safari) we promote the iOS column to the full pane and
 * collapse Android to a footnote, since the iOS visitor has no choice.
 */
function InstallInstructionsDialog({
  open,
  onClose,
  forceIos,
}: {
  open: boolean;
  onClose: () => void;
  forceIos: boolean;
}) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h5" component="span" sx={{ fontWeight: 700 }}>
          {t('pwa.dialog.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t('pwa.dialog.subtitle')}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {/* iOS — always shown */}
        <PlatformBlock
          title={t('pwa.dialog.iosTitle')}
          steps={[
            {
              icon: <IosShareRoundedIcon fontSize="small" />,
              text: t('pwa.dialog.iosStep1'),
            },
            {
              icon: <AddRoundedIcon fontSize="small" />,
              text: t('pwa.dialog.iosStep2'),
            },
            {
              icon: <OpenInNewRoundedIcon fontSize="small" />,
              text: t('pwa.dialog.iosStep3'),
            },
          ]}
        />

        {/* Android — collapsed to a footnote if the visitor is on iOS;
            iOS users can never trigger the Android path, no need to
            occupy their attention with it. */}
        {!forceIos && (
          <Box sx={{ mt: 3 }}>
            <PlatformBlock
              title={t('pwa.dialog.androidTitle')}
              steps={[
                {
                  icon: <MenuRoundedIcon fontSize="small" />,
                  text: t('pwa.dialog.androidStep1'),
                },
                {
                  icon: <GetAppRoundedIcon fontSize="small" />,
                  text: t('pwa.dialog.androidStep2'),
                },
              ]}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="contained" onClick={onClose}>
          {t('common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function PlatformBlock({
  title,
  steps,
}: {
  title: string;
  steps: { icon: React.ReactNode; text: string }[];
}) {
  return (
    <Box>
      <Typography
        variant="overline"
        sx={{
          color: 'primary.main',
          letterSpacing: '0.12em',
          fontWeight: 700,
        }}
      >
        {title}
      </Typography>
      <List dense disablePadding sx={{ mt: 0.5 }}>
        {steps.map((s, i) => (
          <ListItem key={i} disableGutters alignItems="flex-start" sx={{ py: 0.5 }}>
            <ListItemIcon sx={{ minWidth: 32, mt: 0.5, color: 'text.secondary' }}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  {i + 1}.
                </Typography>
                {s.icon}
              </Stack>
            </ListItemIcon>
            <ListItemText
              primaryTypographyProps={{ variant: 'body2', sx: { lineHeight: 1.55 } }}
              primary={s.text}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );
}
